import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  question: string;
  answer: string;
}

/**
 * FAQ
 *
 * Accordion-based FAQ section with:
 * - Smooth expand/collapse animations
 * - Neutral styled accordion items
 * - Entrance animations on scroll
 * - All text from translations
 */
export function FAQ() {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("faq.title");
  const items = t("faq.items", { returnObjects: true }) as FAQItem[];

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="faq" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">FAQ</SectionLabel>

          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div
          className={cn(
            "mx-auto max-w-3xl",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "150ms" : "0ms" }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {Array.isArray(items) &&
              items.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className={cn(
                    "overflow-hidden rounded-2xl",
                    "bg-card",
                    "border border-border",
                    "transition-shadow duration-300",
                  )}
                >
                  <AccordionTrigger
                    className={cn(
                      "px-6 py-5 text-left hover:no-underline",
                      "font-medium text-foreground",
                      "hover:text-foreground",
                      "[&[data-state=open]>svg]:rotate-180",
                      "[&>svg]:text-primary [&>svg]:transition-transform [&>svg]:duration-300",
                    )}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5">
                    <p className="text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
